import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { api, errorMessage } from "../lib/api";
import { FASI, FASE_LABEL, MOMENTO_LABEL, TIPO_LABEL, OBIEZIONE_TAGS, OBIEZIONE_LABEL } from "../lib/constants";

const MOMENTI = ["prima_call", "seconda_call", "call_avanzata", "trasversale"];

export const KnowledgeEditDialog = ({ item, intents = [], onClose, onSaved }) => {
  const [frase, setFrase] = useState("");
  const [titolo, setTitolo] = useState("");
  const [fase, setFase] = useState("apertura");
  const [intentId, setIntentId] = useState("");
  const [momento, setMomento] = useState("trasversale");
  const [tipo, setTipo] = useState("script");
  const [obTag, setObTag] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setFrase(item.frase_rappresentativa || item.testo || "");
      setTitolo(item.titolo || "");
      setFase(item.fase || "apertura");
      setIntentId(item.intent_id || "");
      setMomento(item.momento || "trasversale");
      setTipo(item.tipo || "script");
      setObTag(item.obiezione_tag || "");
    }
  }, [item]);

  const faseIntents = intents.filter((i) => i.fase === fase);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        frase_rappresentativa: frase,
        titolo,
        fase,
        momento,
        tipo,
        obiezione_tag: fase === "gestione_obiezioni" ? (obTag || null) : null,
      };
      if (intentId) payload.intent_id = intentId;
      const { data } = await api.put(`/kb/cluster/${item.id}`, payload);
      onSaved(data);
      toast.success("Cluster aggiornato e re-indicizzato");
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="knowledge-edit-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifica cluster</DialogTitle>
          <DialogDescription>La frase rappresentativa e le etichette verranno salvate; l'embedding viene rigenerato.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Concetto (titolo)</label>
            <input data-testid="edit-titolo-input" value={titolo} onChange={(e) => setTitolo(e.target.value)} className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Fase</label>
              <select data-testid="edit-fase-select" value={fase} onChange={(e) => { setFase(e.target.value); setIntentId(""); }} className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
                {FASI.map((f) => <option key={f} value={f}>{FASE_LABEL[f]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tipo</label>
              <select data-testid="edit-tipo-select" value={tipo} onChange={(e) => setTipo(e.target.value)} className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
                {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Intento</label>
              <select data-testid="edit-intent-select" value={intentId} onChange={(e) => setIntentId(e.target.value)} className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
                <option value="">— mantieni —</option>
                {faseIntents.map((i) => <option key={i.id} value={i.id}>{i.label}{i.stato === "da_confermare" ? " (da confermare)" : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Momento</label>
              <select data-testid="edit-momento-select" value={momento} onChange={(e) => setMomento(e.target.value)} className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
                {MOMENTI.map((m) => <option key={m} value={m}>{MOMENTO_LABEL[m]}</option>)}
              </select>
            </div>
          </div>
          {fase === "gestione_obiezioni" && (
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tag obiezione</label>
              <select data-testid="edit-obtag-select" value={obTag} onChange={(e) => setObTag(e.target.value)} className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
                <option value="">— nessuno —</option>
                {OBIEZIONE_TAGS.map((t) => <option key={t} value={t}>{OBIEZIONE_LABEL[t]}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Frase rappresentativa</label>
            <textarea data-testid="edit-text-input" value={frase} onChange={(e) => setFrase(e.target.value)} rows={4} className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm leading-relaxed outline-none focus:border-primary" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="edit-cancel-btn" onClick={onClose} className="rounded-full px-4 py-2 text-sm text-muted-foreground hover:text-white">Annulla</button>
          <button data-testid="edit-save-btn" onClick={save} disabled={saving || frase.trim().length < 5} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 spin-slow" />} Salva
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
